import { useMutation, useQuery } from '@apollo/client/react'
import { useState } from 'react'

import { useAuth } from '@/auth/useAuth'
import {
  CirclesToJoinForEmailDocument,
  HandleCommunityInvitationDocument,
} from '@/graphql/api/generated'
import { useTracking } from '@/hooks/useTracking'

export type InvitedCommunityEdge = NonNullable<
  NonNullable<ReturnType<typeof useInvitedCommunities>['edges'][number]>
>

/**
 * Pending/accepted community invitations for the freshly registered user.
 *
 * Extracted from the congratulations step so the same block can be reused on the
 * post-registration profile-selection screen. `firstAcceptedCommunitySlug` is exposed
 * so the parent can keep driving dashboard navigation (see useCongratulationsStep).
 */
export const useInvitedCommunities = () => {
  const auth = useAuth()
  const { captureEvent } = useTracking()

  const [acceptedCommunities, setAcceptedCommunities] = useState<string[]>(
    auth.user?.circleMemberships?.edges?.map(
      (edge) => edge!.node!.circle.slug
    ) ?? []
  )

  const { data, loading } = useQuery(CirclesToJoinForEmailDocument, {
    variables: { email: auth.user?.email },
  })

  const edges = data?.circles?.edges ?? []
  const circlesCount = edges.length
  const firstAcceptedCommunitySlug = acceptedCommunities[0]

  const [acceptCommunityInvitation] = useMutation(
    HandleCommunityInvitationDocument
  )

  const handleAcceptCommunityInvitation = (
    slug: string,
    invitationId: string
  ) => {
    captureEvent('registration_community_invitation_accepted', {
      community_slug: slug,
      invitation_id: invitationId,
      // The address is already a person property set by PostHogService.identify(), so
      // repeating it on every event buys nothing and widens what a leaked export contains.
      user_id: auth.user?.id,
    })

    acceptCommunityInvitation({
      variables: {
        id: invitationId,
        decision: 'accept',
      },
    })
    setAcceptedCommunities((prev) => [...prev, slug])
  }

  return {
    loading,
    edges,
    circlesCount,
    acceptedCommunities,
    firstAcceptedCommunitySlug,
    handleAcceptCommunityInvitation,
  }
}
